import {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {verifyPasswordResetCode, confirmPasswordReset} from 'firebase/auth'
import {auth} from '../../../../firebaseConfig'
import {useSnackbar} from 'notistack'

const initialValues = {
  password: '',
  confirmPassword: '',
}

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be atleast 6 characters of length'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords do not match'),
})

const errorMessageFor = (code: string) => {
  switch (code) {
    case 'auth/expired-action-code':
      return 'This password reset link has expired. Please request a new one.'
    case 'auth/invalid-action-code':
      return 'This password reset link is invalid or has already been used. Please request a new one.'
    case 'auth/weak-password':
      return 'That password is too weak. Please choose a stronger one.'
    default:
      return 'Something went wrong. Please try to reset the password again.'
  }
}

const ResetPassword = () => {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [codeState, setCodeState] = useState<'verifying' | 'valid' | 'invalid'>('verifying')
  const navigate = useNavigate()
  const {enqueueSnackbar} = useSnackbar()

  const actionCode = searchParams.get('oobCode') || ''

  // Check the link up front so an expired one is reported immediately, rather than
  // after the user has already picked and typed a new password.
  useEffect(() => {
    let cancelled = false

    if (!actionCode) {
      setCodeState('invalid')
      return
    }

    verifyPasswordResetCode(auth, actionCode)
      .then(() => {
        if (!cancelled) setCodeState('valid')
      })
      .catch(() => {
        if (!cancelled) setCodeState('invalid')
      })

    return () => {
      cancelled = true
    }
  }, [actionCode])

  const formik = useFormik({
    initialValues,
    validationSchema: resetPasswordSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)

      try {
        // The code was already verified on mount, so go straight to confirming.
        await confirmPasswordReset(auth, actionCode, values.password)
        enqueueSnackbar('Password Changed Successfully. Please login', {variant: 'success'})
        navigate('/auth/login')
      } catch (error: any) {
        setHasErrors(true)
        setErrorMessage(errorMessageFor(error?.code))
        // A code that died between mount and submit can't be recovered by retrying
        // the form — send the user back for a fresh link.
        if (
          error?.code === 'auth/expired-action-code' ||
          error?.code === 'auth/invalid-action-code'
        ) {
          setCodeState('invalid')
        }
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  if (codeState === 'verifying') {
    return (
      <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
        <div className='text-center'>
          <span className='spinner-border text-primary align-middle me-3'></span>
          <span className='text-gray-600 fw-bold fs-5'>Verifying your reset link...</span>
        </div>
      </div>
    )
  }

  if (codeState === 'invalid') {
    return (
      <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
        <div className='text-center mb-10'>
          <h1 className='text-dark mb-3'>Link Expired</h1>
          <div className='text-gray-400 fw-bold fs-4'>
            {errorMessage ||
              'This password reset link is invalid or has expired. Please request a new one.'}
          </div>
        </div>
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <Link to='/auth/forgot-password'>
            <button type='button' className='btn btn-sm btn-primary me-4'>
              Request a new link
            </button>
          </Link>
          <Link to='/auth/login'>
            <button type='button' className='btn btn-sm btn-light-primary'>
              Back to Login
            </button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-10'>
          {/* begin::Title */}
          <h1 className='text-dark mb-3'>Reset Password</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-gray-400 fw-bold fs-4'>Enter your new password below</div>
          {/* end::Link */}
        </div>

        {/* begin::Title */}
        {hasErrors === true && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{errorMessage}</div>
          </div>
        )}
        {/* end::Title */}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <label className='form-label fw-bold text-gray-900 fs-6'>New Password</label>
          <input
            type='password'
            placeholder=''
            autoComplete='new-password'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.password && formik.errors.password},
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <label className='form-label fw-bold text-gray-900 fs-6'>Confirm Password</label>
          <input
            type='password'
            placeholder=''
            autoComplete='new-password'
            {...formik.getFieldProps('confirmPassword')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword},
              {
                'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
              }
            )}
          />
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.confirmPassword}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <button
            type='submit'
            id='kt_password_reset_submit'
            className='btn btn-sm btn-primary me-4'
            disabled={loading}
          >
            <span className='indicator-label'>Update Password</span>
            {loading && (
              <span className='indicator-progress'>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/auth/login'>
            <button type='button' className='btn btn-sm btn-light-primary'>
              Cancel
            </button>
          </Link>{' '}
        </div>
        {/* end::Form group */}
      </form>
    </div>
  )
}

export default ResetPassword
